import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDBadge from "components/MDBadge";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import { oTieuDeCot, AN_DUOI_MD, chuXuongDong } from "lib/kieuBangDuLieu";
import { useDialogFullManHinh, oDialogContent, oDialogActions } from "lib/dungDialogFullManHinh";
import { nguoiDungApi } from "api/nguoiDung";
import { useAuth } from "context/AuthContext";
import { nhanVaiTro, DANH_SACH_VAI_TRO } from "lib/nhanVaiTro";
import { MAU, SEMANTIC, RADIUS, SPACE, XANH_LA, LAYOUT } from "assets/theme/avocadoBrand";
import useTour from "hooks/useTour";
import buocQuanLyTaiKhoan from "tours/quanLyTaiKhoan";

const RONG = { email: "", hoTen: "", vaiTro: "nhan_vien", matKhauTam: "" };

const oTieuDeCotTo = (theme) => ({
  ...oTieuDeCot(theme),
  fontSize: "0.75rem",
  letterSpacing: "0.08em",
  py: 1.5,
  backgroundColor: SEMANTIC.border,
});

const oOBang = {
  py: 1.75,
  px: 2,
  borderBottom: `1px solid ${SEMANTIC.border}`,
  verticalAlign: "middle",
};

const oSelectVaiTro = {
  display: "block",
  width: "100%",
  maxWidth: LAYOUT.filterSelectWidth,
  minWidth: "9.5rem",
  fontSize: "0.875rem",
  fontFamily: "inherit",
  lineHeight: 1.4,
  padding: `${SPACE[2]}px ${SPACE[3]}px`,
  borderRadius: `${RADIUS.input}px`,
  border: `1px solid ${SEMANTIC.borderStrong}`,
  backgroundColor: SEMANTIC.surface,
  color: SEMANTIC.textPrimary,
  cursor: "pointer",
  appearance: "auto",
  "&:hover:not(:disabled)": {
    borderColor: XANH_LA.main,
  },
  "&:focus": {
    outline: `2px solid ${XANH_LA.main}`,
    outlineOffset: 1,
    borderColor: XANH_LA.main,
  },
  "&:disabled": {
    opacity: 0.55,
    cursor: "not-allowed",
    backgroundColor: MAU.paper,
  },
};

const oNutIcon = {
  color: SEMANTIC.textSecondary,
  borderRadius: `${RADIUS.input}px`,
  "&:hover": {
    color: XANH_LA.main,
    backgroundColor: MAU.paper,
  },
  "&.Mui-disabled": { opacity: 0.4 },
};

function SelectVaiTro({ value, disabled, onChange, id }) {
  return (
    <MDBox
      component="select"
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      sx={oSelectVaiTro}
      aria-label="Đổi vai trò"
    >
      {DANH_SACH_VAI_TRO.map((vt) => (
        <option key={vt} value={vt}>
          {nhanVaiTro(vt)}
        </option>
      ))}
    </MDBox>
  );
}

SelectVaiTro.propTypes = {
  value: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string,
};

SelectVaiTro.defaultProps = {
  disabled: false,
  id: undefined,
};

function ThongTinPhu({ nguoiDung }) {
  const phan = [
    nguoiDung.email,
    nguoiDung.sdt,
    nhanVaiTro(nguoiDung.vaiTro),
    nguoiDung.voHieuHoa ? "Đã vô hiệu hoá" : "Hoạt động",
  ].filter(Boolean);
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
      sx={chuXuongDong}
    >
      {phan.join(" · ")}
    </MDTypography>
  );
}

ThongTinPhu.propTypes = {
  nguoiDung: PropTypes.shape({
    email: PropTypes.string,
    sdt: PropTypes.string,
    vaiTro: PropTypes.string,
    voHieuHoa: PropTypes.bool,
  }).isRequired,
};

function QuanLyTaiKhoan() {
  const fullManHinh = useDialogFullManHinh();
  const { user } = useAuth();
  const [danhSach, setDanhSach] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");
  const [thongBao, setThongBao] = useState("");
  const [dangLuuId, setDangLuuId] = useState(null);
  const [dangGuiResetId, setDangGuiResetId] = useState(null);
  const [dangDoiTrangThaiId, setDangDoiTrangThaiId] = useState(null);

  const [modalTaoMoi, setModalTaoMoi] = useState(null);
  const [dangTaoTaiKhoan, setDangTaoTaiKhoan] = useState(false);
  const [taiKhoanVuaTao, setTaiKhoanVuaTao] = useState(null);

  const [modalSua, setModalSua] = useState(null);
  const [dangLuuSua, setDangLuuSua] = useState(false);

  async function taiLai() {
    // Realtime onSnapshot cập nhật danh sách.
  }

  useEffect(() => {
    setDangTai(true);
    const huy = nguoiDungApi.langNgheDanhSach(
      (ds) => {
        setDanhSach(ds);
        setDangTai(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTai(false);
      }
    );
    return huy;
  }, []);

  async function doiVaiTro(id, vaiTroMoi) {
    setDangLuuId(id);
    setLoi("");
    try {
      await nguoiDungApi.doiVaiTro(id, vaiTroMoi);
      setDanhSach((ds) => ds.map((nd) => (nd.id === id ? { ...nd, vaiTro: vaiTroMoi } : nd)));
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuuId(null);
    }
  }

  async function taoTaiKhoan(e) {
    e.preventDefault();
    setDangTaoTaiKhoan(true);
    setLoi("");
    try {
      await nguoiDungApi.taoTaiKhoan(modalTaoMoi);
      setTaiKhoanVuaTao(modalTaoMoi);
      setModalTaoMoi(null);
      await taiLai();
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangTaoTaiKhoan(false);
    }
  }

  async function doiTrangThaiHoatDong(nd) {
    const voHieuHoaMoi = !nd.voHieuHoa;
    if (
      voHieuHoaMoi &&
      // eslint-disable-next-line no-alert
      !window.confirm(`Vô hiệu hoá tài khoản ${nd.email}? Người này sẽ bị đăng xuất ngay lập tức.`)
    ) {
      return;
    }
    setDangDoiTrangThaiId(nd.id);
    setLoi("");
    try {
      await nguoiDungApi.datTrangThaiHoatDong(nd.id, voHieuHoaMoi);
      setDanhSach((ds) => ds.map((x) => (x.id === nd.id ? { ...x, voHieuHoa: voHieuHoaMoi } : x)));
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangDoiTrangThaiId(null);
    }
  }

  async function luuSua(e) {
    e.preventDefault();
    setDangLuuSua(true);
    setLoi("");
    try {
      await nguoiDungApi.capNhatThongTin(modalSua.id, { hoTen: modalSua.hoTen, sdt: modalSua.sdt });
      setDanhSach((ds) =>
        ds.map((nd) =>
          nd.id === modalSua.id ? { ...nd, hoTen: modalSua.hoTen, sdt: modalSua.sdt } : nd
        )
      );
      setModalSua(null);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuuSua(false);
    }
  }

  async function guiEmailReset(nd) {
    setDangGuiResetId(nd.id);
    setLoi("");
    setThongBao("");
    try {
      await nguoiDungApi.guiEmailDatLaiMatKhau(nd.email);
      setThongBao(`Đã gửi email đặt lại mật khẩu tới ${nd.email}.`);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangGuiResetId(null);
    }
  }

  useTour("quan-ly-tai-khoan", buocQuanLyTaiKhoan());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Quản lý"
          tieuDe="Tài khoản"
          moTa="Tạo tài khoản, đổi vai trò, hoặc gửi email đặt lại mật khẩu cho nhân sự."
        >
          <MDButton
            id="tour-quan-ly-tai-khoan-tao-moi"
            variant="gradient"
            color="info"
            onClick={() => {
              setLoi("");
              setModalTaoMoi({ ...RONG });
            }}
          >
            <Icon sx={{ mr: 0.75 }}>person_add</Icon>
            Tạo tài khoản
          </MDButton>
        </DauTrang>

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}
        {thongBao && (
          <MDBox mb={2}>
            <MDAlert color="success">{thongBao}</MDAlert>
          </MDBox>
        )}

        <Card
          id="tour-quan-ly-tai-khoan-bang"
          sx={{
            borderRadius: `${RADIUS.card}px`,
            border: `1px solid ${SEMANTIC.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <MDBox
            px={2.5}
            py={2}
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{ borderBottom: `1px solid ${SEMANTIC.border}`, backgroundColor: MAU.paper }}
          >
            <MDBox>
              <NhanDau>Nhân sự</NhanDau>
              <MDTypography variant="button" fontWeight="medium" color="dark">
                {danhSach.length} tài khoản đăng nhập
              </MDTypography>
            </MDBox>
          </MDBox>

          {dangTai ? (
            <MDBox px={2.5} py={4}>
              <MDTypography variant="button" color="text">
                Đang tải...
              </MDTypography>
            </MDBox>
          ) : danhSach.length === 0 ? (
            <MDBox px={2.5} py={5} textAlign="center">
              <Icon sx={{ color: SEMANTIC.textDisabled, fontSize: "2.5rem" }}>badge</Icon>
              <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                Chưa có tài khoản nào.
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                Bấm “Tạo tài khoản” để thêm nhân sự đầu tiên.
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: { xs: 0, md: 720 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={oTieuDeCotTo}>Họ tên</TableCell>
                    <TableCell sx={[oTieuDeCotTo, AN_DUOI_MD]}>Email</TableCell>
                    <TableCell sx={[oTieuDeCotTo, AN_DUOI_MD]}>SĐT</TableCell>
                    <TableCell sx={[oTieuDeCotTo, AN_DUOI_MD]}>Vai trò</TableCell>
                    <TableCell sx={[oTieuDeCotTo, AN_DUOI_MD]}>Trạng thái</TableCell>
                    <TableCell align="right" sx={oTieuDeCotTo}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {danhSach.map((nd) => {
                    const laChinhMinh = nd.id === user?.uid;
                    const dangLuuVaiTro = dangLuuId === nd.id;
                    return (
                      <TableRow
                        key={nd.id}
                        sx={{
                          opacity: nd.voHieuHoa ? 0.72 : 1,
                          transition: "background-color 160ms ease",
                          "&:hover": { backgroundColor: MAU.paper },
                          "&:last-of-type td": { borderBottom: 0 },
                        }}
                      >
                        <TableCell sx={oOBang}>
                          <MDTypography
                            variant="button"
                            fontWeight="medium"
                            display="block"
                            sx={{ ...chuXuongDong, color: SEMANTIC.textPrimary }}
                          >
                            {nd.hoTen ?? nd.email}
                          </MDTypography>
                          <ThongTinPhu nguoiDung={nd} />
                          <MDBox display={{ xs: "block", md: "none" }} mt={1}>
                            <Tooltip
                              title={laChinhMinh ? "Không thể tự đổi vai trò của chính mình" : ""}
                            >
                              <span>
                                <SelectVaiTro
                                  value={nd.vaiTro ?? "nhan_vien"}
                                  disabled={laChinhMinh || dangLuuVaiTro}
                                  onChange={(vt) => doiVaiTro(nd.id, vt)}
                                />
                              </span>
                            </Tooltip>
                          </MDBox>
                        </TableCell>
                        <TableCell sx={[oOBang, AN_DUOI_MD, chuXuongDong]}>
                          <MDTypography variant="button" color="text" fontWeight="regular">
                            {nd.email}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={[oOBang, AN_DUOI_MD]}>
                          <MDTypography variant="button" color="text" fontWeight="regular">
                            {nd.sdt || "—"}
                          </MDTypography>
                        </TableCell>
                        <TableCell sx={[oOBang, AN_DUOI_MD]}>
                          <Tooltip
                            title={laChinhMinh ? "Không thể tự đổi vai trò của chính mình" : ""}
                          >
                            <span>
                              <SelectVaiTro
                                value={nd.vaiTro ?? "nhan_vien"}
                                disabled={laChinhMinh || dangLuuVaiTro}
                                onChange={(vt) => doiVaiTro(nd.id, vt)}
                              />
                            </span>
                          </Tooltip>
                          {dangLuuVaiTro && (
                            <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                              Đang lưu...
                            </MDTypography>
                          )}
                        </TableCell>
                        <TableCell sx={[oOBang, AN_DUOI_MD]}>
                          <MDBadge
                            badgeContent={nd.voHieuHoa ? "Đã vô hiệu hoá" : "Hoạt động"}
                            color={nd.voHieuHoa ? "secondary" : "success"}
                            variant="gradient"
                            size="sm"
                          />
                        </TableCell>
                        <TableCell align="right" sx={oOBang}>
                          <MDBox display="flex" justifyContent="flex-end" gap={0.25}>
                            <Tooltip title="Sửa thông tin">
                              <IconButton
                                size="small"
                                sx={oNutIcon}
                                aria-label="Sửa"
                                onClick={() => {
                                  setLoi("");
                                  setModalSua({
                                    id: nd.id,
                                    hoTen: nd.hoTen ?? "",
                                    sdt: nd.sdt ?? "",
                                    email: nd.email ?? "",
                                  });
                                }}
                              >
                                <Icon fontSize="small">edit</Icon>
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Gửi email đặt lại mật khẩu">
                              <span>
                                <IconButton
                                  size="small"
                                  sx={oNutIcon}
                                  aria-label="Gửi email đặt lại mật khẩu"
                                  disabled={dangGuiResetId === nd.id}
                                  onClick={() => guiEmailReset(nd)}
                                >
                                  <Icon fontSize="small">
                                    {dangGuiResetId === nd.id ? "hourglass_empty" : "lock_reset"}
                                  </Icon>
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                laChinhMinh
                                  ? "Không thể tự vô hiệu hoá chính mình"
                                  : nd.voHieuHoa
                                  ? "Kích hoạt lại"
                                  : "Vô hiệu hoá"
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={nd.voHieuHoa ? "Kích hoạt lại" : "Vô hiệu hoá"}
                                  disabled={laChinhMinh || dangDoiTrangThaiId === nd.id}
                                  onClick={() => doiTrangThaiHoatDong(nd)}
                                  sx={{
                                    ...oNutIcon,
                                    color: nd.voHieuHoa ? SEMANTIC.success : SEMANTIC.error,
                                    "&:hover": {
                                      color: nd.voHieuHoa ? SEMANTIC.success : SEMANTIC.error,
                                      backgroundColor: MAU.paper,
                                    },
                                  }}
                                >
                                  <Icon fontSize="small">
                                    {dangDoiTrangThaiId === nd.id
                                      ? "hourglass_empty"
                                      : nd.voHieuHoa
                                      ? "restart_alt"
                                      : "person_off"}
                                  </Icon>
                                </IconButton>
                              </span>
                            </Tooltip>
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />

      <Dialog
        open={!!modalTaoMoi}
        onClose={() => setModalTaoMoi(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullManHinh}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, ...chuXuongDong }}>
          <NhanDau>Thêm mới</NhanDau>
          <MDTypography variant="h5" component="h2">
            Tài khoản
          </MDTypography>
        </DialogTitle>
        <MDBox component="form" onSubmit={taoTaiKhoan}>
          <DialogContent sx={oDialogContent}>
            {loi && (
              <MDBox mb={2}>
                <MDAlert color="error">{loi}</MDAlert>
              </MDBox>
            )}
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                value={modalTaoMoi?.email ?? ""}
                onChange={(e) => setModalTaoMoi({ ...modalTaoMoi, email: e.target.value })}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                label="Họ tên"
                value={modalTaoMoi?.hoTen ?? ""}
                onChange={(e) => setModalTaoMoi({ ...modalTaoMoi, hoTen: e.target.value })}
                fullWidth
                required
              />
            </MDBox>
            <MDBox mb={2}>
              <MDTypography
                variant="caption"
                color="text"
                fontWeight="medium"
                display="block"
                mb={0.5}
              >
                Vai trò *
              </MDTypography>
              <SelectVaiTro
                value={modalTaoMoi?.vaiTro ?? "nhan_vien"}
                onChange={(vt) => setModalTaoMoi({ ...modalTaoMoi, vaiTro: vt })}
              />
            </MDBox>
            <MDBox mb={1}>
              <MDInput
                type="text"
                label="Mật khẩu tạm (ít nhất 6 ký tự)"
                value={modalTaoMoi?.matKhauTam ?? ""}
                onChange={(e) => setModalTaoMoi({ ...modalTaoMoi, matKhauTam: e.target.value })}
                fullWidth
                required
                inputProps={{ minLength: 6 }}
              />
            </MDBox>
            <MDTypography variant="caption" color="text" sx={chuXuongDong}>
              Gửi mật khẩu này cho nhân sự qua kênh riêng (không phải email tự động). Sau đó có thể
              dùng nút đặt lại mật khẩu để họ tự đổi.
            </MDTypography>
          </DialogContent>
          <DialogActions sx={oDialogActions}>
            <MDButton variant="outlined" color="secondary" onClick={() => setModalTaoMoi(null)}>
              Huỷ
            </MDButton>
            <MDButton type="submit" variant="gradient" color="info" disabled={dangTaoTaiKhoan}>
              {dangTaoTaiKhoan ? "Đang tạo..." : "Tạo tài khoản"}
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>

      <Dialog
        open={!!modalSua}
        onClose={() => setModalSua(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullManHinh}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, ...chuXuongDong }}>
          <NhanDau>Cập nhật</NhanDau>
          <MDTypography variant="h5" component="h2">
            Thông tin nhân sự
          </MDTypography>
        </DialogTitle>
        {modalSua && (
          <MDBox component="form" onSubmit={luuSua}>
            <DialogContent sx={oDialogContent}>
              {loi && (
                <MDBox mb={2}>
                  <MDAlert color="error">{loi}</MDAlert>
                </MDBox>
              )}
              <MDBox mb={2}>
                <MDInput
                  label="Họ tên"
                  value={modalSua.hoTen}
                  onChange={(e) => setModalSua({ ...modalSua, hoTen: e.target.value })}
                  fullWidth
                  required
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  label="Số điện thoại"
                  value={modalSua.sdt}
                  onChange={(e) => setModalSua({ ...modalSua, sdt: e.target.value })}
                  fullWidth
                />
              </MDBox>
              <MDBox mb={1}>
                <MDInput type="email" label="Email" value={modalSua.email} disabled fullWidth />
              </MDBox>
              <MDTypography variant="caption" color="text" sx={chuXuongDong}>
                Không đổi được email đăng nhập tại đây — cần vào Firebase Console (Authentication)
                để đổi thủ công.
              </MDTypography>
            </DialogContent>
            <DialogActions sx={oDialogActions}>
              <MDButton variant="outlined" color="secondary" onClick={() => setModalSua(null)}>
                Huỷ
              </MDButton>
              <MDButton type="submit" variant="gradient" color="info" disabled={dangLuuSua}>
                {dangLuuSua ? "Đang lưu..." : "Lưu"}
              </MDButton>
            </DialogActions>
          </MDBox>
        )}
      </Dialog>

      <Dialog
        open={!!taiKhoanVuaTao}
        onClose={() => setTaiKhoanVuaTao(null)}
        fullWidth
        maxWidth="sm"
        fullScreen={fullManHinh}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, ...chuXuongDong }}>
          <NhanDau>Đã tạo</NhanDau>
          <MDTypography variant="h5" component="h2">
            Tài khoản mới
          </MDTypography>
          <IconButton
            onClick={() => setTaiKhoanVuaTao(null)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={oDialogContent}>
          <MDTypography variant="button" color="text" display="block" mb={1} sx={chuXuongDong}>
            Email: <b>{taiKhoanVuaTao?.email}</b>
          </MDTypography>
          <MDTypography variant="button" color="text" display="block" mb={1} sx={chuXuongDong}>
            Mật khẩu tạm: <b>{taiKhoanVuaTao?.matKhauTam}</b>
          </MDTypography>
          <MDTypography variant="caption" color="error" sx={chuXuongDong}>
            Mật khẩu này chỉ hiện 1 lần — hãy gửi cho nhân sự ngay bây giờ.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={oDialogActions}>
          <MDButton variant="outlined" color="secondary" onClick={() => setTaiKhoanVuaTao(null)}>
            Đóng
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default QuanLyTaiKhoan;
